/**
 * Workshop Detail Page
 * Shows workshop information and list of exercises
 */

import { Link, useParams } from "react-router-dom";
import { ExerciseList } from "@components/workshop";
import { useWorkshop, useWorkshopExercises, useMyProgress } from "@hooks";
import type { Progress, Exercise } from "@/types";

export function WorkshopDetailPage() {
  const { id } = useParams<{ id: string }>();

  const {
    data: workshop,
    isLoading: workshopLoading,
    error: workshopError,
  } = useWorkshop(id);
  const { data: exercises = [], isLoading: exercisesLoading } =
    useWorkshopExercises(id);
  const { data: progress = [] } = useMyProgress();

  // Get completed exercise IDs for this workshop
  const completedExerciseIds = progress
    .filter(
      (p: Progress) =>
        p.status === "completed" &&
        exercises.some((e: Exercise) => e.id === p.exercise_id),
    )
    .map((p: Progress) => p.exercise_id);

  const completedCount = completedExerciseIds.length;
  const totalCount = exercises.length;
  const percentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const isLoading = workshopLoading || exercisesLoading;

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-2" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-8" />
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded mb-8" />
          <div className="space-y-4">
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (workshopError || !workshop) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Workshop Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The workshop you're looking for doesn't exist or you don't have
            access to it.
          </p>
          <Link
            to="/workshops"
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            Back to Workshops
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
        <Link
          to="/"
          className="hover:text-primary-600 dark:hover:text-primary-400"
        >
          Dashboard
        </Link>
        <span>/</span>
        <Link
          to="/workshops"
          className="hover:text-primary-600 dark:hover:text-primary-400"
        >
          Workshops
        </Link>
        <span>/</span>
        <span className="text-gray-900 dark:text-white">{workshop.title}</span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {workshop.title}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {workshop.description}
        </p>
      </div>

      {/* Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            Your Progress
          </h2>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {completedCount} of {totalCount} exercises completed
          </span>
        </div>
        <div
          className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-primary-500 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Exercises */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Exercises
        </h2>
        <ExerciseList
          exercises={exercises}
          completedIds={completedExerciseIds}
        />
      </div>

      {/* Back button */}
      <div className="mt-8">
        <Link
          to="/workshops"
          className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Workshops
        </Link>
      </div>
    </div>
  );
}
